import { useRef, useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CreditCard, RefreshCcw, Check, AlertTriangle, Settings } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { authService, useRenewDetails, usePlanDetails } from "@/lib/api";

const currentPlan = {
  name: "Basic Plan",
  price: "₹2,500",
  priceNote: "+ GST",
  period: "year",
  nextBillingDate: "December 15, 2025",
  dataRefresh: "Daily",
  usage: "78%",
};

// Helper function to transform API plan data to component format
const transformPlanData = (apiPlans: any[], currentPlanId?: number) => {
  return apiPlans.map((plan, index) => {
    // Parse planFeatures string (separated by \r\n, features end with semicolon) into array
    const features = plan.planFeatures
      .split(/\r\n|\n/)
      .map((f: string) => f.trim().replace(/;+$/, "")) // Remove trailing semicolons
      .filter((f: string) => f.length > 0);

    // Format price
    const formattedPrice = formatPrice(plan.planValue);

    // Map planMode to period
    const period = plan.planMode.toLowerCase() === "yearly" ? "year" : plan.planMode.toLowerCase();

    // Determine if this is the current plan
    const isCurrent = currentPlanId === plan.planId;

    // Mark middle plan (Core) as popular, or if there are 3 plans, mark the middle one
    const isPopular = apiPlans.length === 3 && index === 1;

    return {
      name: plan.planName,
      price: formattedPrice,
      priceNote: "+ GST",
      period: period,
      features: features,
      isCurrent: isCurrent,
      isPopular: isPopular,
      planId: plan.planId,
      planValue: plan.planValue,
    };
  });
};

const formatRenewDate = (dateString: string): string => {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
};

const formatPrice = (value: number): string => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
};

const MyAccount = () => {
  const [activeTab, setActiveTab] = useState<"account" | "billing">("account");

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const nameRef = useRef<HTMLInputElement | null>(null);
  const numberRef = useRef<HTMLInputElement | null>(null);
  const expiryRef = useRef<HTMLInputElement | null>(null);
  const cvvRef = useRef<HTMLInputElement | null>(null);
  
  // Get renewDetails for billing date
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;
  const firmIdNumber = firmId ? Number(firmId) : undefined;
  const { data: renewDetails } = useRenewDetails({ firmId: firmIdNumber });
  const nextBillingDate = renewDetails?.renewDate ? formatRenewDate(renewDetails.renewDate) : currentPlan.nextBillingDate;
  
  // Get plan details from API
  const { data: planDetailsData, isLoading: isLoadingPlans } = usePlanDetails();
  
  // Transform API plan data to component format
  const availablePlans = planDetailsData 
    ? transformPlanData(planDetailsData, renewDetails?.planId)
    : [];
  
  // Get plan details from API or use defaults
  const planName = renewDetails?.planName || currentPlan.name;
  const planPrice = renewDetails?.planValue ? formatPrice(renewDetails.planValue) : currentPlan.price;
  const dataRefresh = renewDetails?.dateRefresh || currentPlan.dataRefresh;
  const dataUsage = renewDetails?.dataUsage !== undefined ? `${renewDetails.dataUsage}%` : currentPlan.usage;
  
  // Determine plan status based on isExpired and renewDate
  const getPlanStatus = () => {
    if (!renewDetails) return { status: "Active", className: "bg-primary/10 text-primary border-primary/20" };
    
    if (renewDetails.isExpired === 0) {
      return { status: "Active", className: "bg-primary/10 text-primary border-primary/20" };
    }
    
    // isExpired === 1
    if (renewDetails.renewDate) {
      const renewDate = new Date(renewDetails.renewDate);
      const currentDate = new Date();
      // Set time to midnight for accurate date comparison
      renewDate.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);
      
      if (renewDate <= currentDate) {
        return { status: "Expired", className: "bg-destructive/10 text-destructive border-destructive/20" };
      } else {
        return { status: "Expiry Soon", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" };
      }
    }
    
    // Default to Expired if isExpired === 1 but no renewDate
    return { status: "Expired", className: "bg-destructive/10 text-destructive border-destructive/20" };
  };
  
  const planStatus = getPlanStatus();

  const handlePay = () => {
    const trimmedNumber = cardNumber.replace(/\s+/g, "");
    const errors: string[] = [];
    if (!cardholderName.trim()) errors.push("Cardholder name is required.");
    if (trimmedNumber.length < 12) errors.push("Enter a valid card number.");
    if (!/^\d{2}\/\d{2}$/.test(expiry)) errors.push("Expiry must be MM/YY.");
    if (!/^\d{3,4}$/.test(cvv)) errors.push("CVV must be 3-4 digits.");

    if (errors.length) {
      if (!cardholderName.trim()) {
        nameRef.current?.focus();
      } else if (trimmedNumber.length < 12) {
        numberRef.current?.focus();
      } else if (!/^\d{2}\/\d{2}$/.test(expiry)) {
        expiryRef.current?.focus();
      } else if (!/^\d{3,4}$/.test(cvv)) {
        cvvRef.current?.focus();
      }
      toast({
        title: "Please fix the following:",
        description: errors.join(" "),
        variant: "destructive",
      });
      return;
    }

    // Simulate payment success then navigate
    navigate("/success");
  };

  const billingDetails = {
    subtotal: 29.0,
    tax: 2.9,
    total: 31.9,
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Header Section */}
        <div className="border-b bg-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">My Account</h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Manage your Pharmasoft CS Director subscription and billing details.
                </p>
              </div>
              <div className="hidden md:flex items-center gap-3 lg:gap-4">
                <Button
                  variant={activeTab === "account" ? "default" : "ghost"}
                  className={
                    activeTab === "account"
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "text-muted-foreground hover:text-foreground"
                  }
                  onClick={() => setActiveTab("account")}
                >
                  My Account
                </Button>
                <Button
                  variant={activeTab === "billing" ? "default" : "ghost"}
                  className={
                    activeTab === "billing"
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "text-muted-foreground hover:text-foreground"
                  }
                  onClick={() => navigate("/payment")}
                >
                  Billing
                </Button>
                <Button
                  variant="ghost"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => navigate("/transactions-list")}
                >
                  Transactions List
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8">
          {activeTab === "billing" ? (
            <div className="space-y-6">
              <button
                type="button"
                onClick={() => setActiveTab("account")}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <span className="text-lg">←</span>
                Back to Account
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 sm:gap-6">
                <Card className="shadow-sm border-border/60">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20 flex-shrink-0">
                        <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-lg sm:text-xl text-foreground">Payment Details</CardTitle>
                        <CardDescription className="text-xs sm:text-sm">Renew your Pro subscription</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-1.5">
                      <p className="text-sm text-muted-foreground">Cardholder Name</p>
                      <Input
                        placeholder="John Doe"
                        value={cardholderName}
                        onChange={(e) => setCardholderName(e.target.value)}
                        ref={nameRef}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-sm text-muted-foreground">Card Number</p>
                      <Input
                        placeholder="4242 4242 4242 4242"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        ref={numberRef}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-1.5">
                        <p className="text-sm text-muted-foreground">Expiry Date</p>
                        <Input placeholder="MM/YY" value={expiry} onChange={(e) => setExpiry(e.target.value)} ref={expiryRef} />
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-sm text-muted-foreground">CVV</p>
                        <Input placeholder="123" value={cvv} onChange={(e) => setCvv(e.target.value)} ref={cvvRef} />
                      </div>
                    </div>

                    <div className="pt-2 space-y-2 text-sm text-foreground">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>${billingDetails.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax</span>
                        <span>${billingDetails.tax.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-base font-semibold">
                        <span className="text-foreground">Total</span>
                        <span>${billingDetails.total.toFixed(2)}</span>
                      </div>
                    </div>

                    <Button className="w-full gradient-primary hover:opacity-90 transition-opacity" onClick={handlePay}>
                      Pay ${billingDetails.total.toFixed(2)}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      Your payment is secured with 256-bit SSL encryption
                    </p>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-foreground">Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-lg border bg-muted/40 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Pro Plan</p>
                          <p className="text-xs text-muted-foreground">Monthly subscription</p>
                        </div>
                        <span className="text-lg font-semibold text-foreground">$29.00</span>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span className="text-primary">○</span>
                        <span>30-day money back guarantee</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-primary">○</span>
                        <span>Secure SSL encryption</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-border/70">
                      <h3 className="text-sm font-semibold text-foreground mb-2">What's included:</h3>
                      <ul className="space-y-1.5 text-sm text-muted-foreground">
                        {[
                          "Unlimited projects",
                          "Advanced analytics",
                          "Priority support",
                          "Custom integrations",
                          "Team collaboration",
                        ].map((item) => (
                          <li key={item} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <>
              {/* Current Plan Section */}
              <Card className="shadow-md border-border/60 bg-card">
                <CardContent className="relative overflow-hidden p-5 sm:p-6 lg:p-8">
                  {/* Decorative watermark / illustrations */}
                  <div className="pointer-events-none absolute inset-0 opacity-80">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.10),transparent_45%),radial-gradient(circle_at_80%_40%,rgba(14,165,233,0.10),transparent_40%),radial-gradient(circle_at_60%_80%,rgba(59,130,246,0.08),transparent_35%)]" />
                    <div className="absolute -left-10 -top-10 h-44 w-44 rounded-full bg-gradient-to-br from-primary/15 via-primary/8 to-transparent blur-3xl animate-[pulse_8s_ease-in-out_infinite]" />
                    <div className="absolute -right-16 top-1/3 h-48 w-48 rounded-full bg-gradient-to-br from-accent/15 via-accent/8 to-transparent blur-3xl animate-[pulse_10s_ease-in-out_infinite]" />
                    <div className="absolute left-1/3 bottom-0 h-28 w-28 rounded-full bg-gradient-to-br from-primary/10 to-transparent blur-2xl animate-[pulse_12s_ease-in-out_infinite]" />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-6 lg:gap-8 items-center relative z-10">
                    {/* Active Plan Section - Left */}
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center border-2 border-primary/20 shadow-sm">
                          <CreditCard className="w-7 h-7" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border-2 whitespace-nowrap ${planStatus.className}`}>
                            {planStatus.status}
                          </span>
                        </div>
                        <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 truncate">
                          {planName}
                        </h3>
                        <div className="flex items-baseline gap-2 mb-2">
                          <span className="text-2xl sm:text-3xl font-bold text-foreground">{planPrice}</span>
                          <span className="text-sm text-muted-foreground">{currentPlan.priceNote}</span>
                          <span className="text-sm text-muted-foreground">/{currentPlan.period}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Next billing: <span className="font-semibold text-foreground">{nextBillingDate}</span>
                        </p>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-24 bg-gradient-to-b from-transparent via-border to-transparent"></div>

                    {/* Data Refresh Section - Center */}
                    <div className="flex items-center justify-center lg:justify-center">
                      <div className="flex flex-col items-center gap-3 px-6 py-5 rounded-xl bg-gradient-to-br from-muted/50 to-muted/30 border-2 border-border/50 shadow-sm min-w-[160px]">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center border-2 border-primary/20 shadow-sm">
                          <RefreshCcw className="w-6 h-6" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                            Data Refresh
                          </p>
                          <p className="text-xl font-bold text-foreground">{dataRefresh}</p>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-24 bg-gradient-to-b from-transparent via-border to-transparent"></div>

                    {/* Action Buttons - Right */}
                    <div className="flex flex-col gap-3 flex-shrink-0 w-full lg:w-auto">
                      <Button
                        className="w-full lg:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md transition-shadow font-medium"
                        onClick={() => {
                          const currentPlanData = availablePlans.find(p => p.isCurrent) || availablePlans[0];
                          if (currentPlanData) {
                            navigate("/payment", { 
                              state: { 
                                plan: {
                                  name: planName,
                                  price: planPrice,
                                  priceNote: currentPlanData.priceNote,
                                  period: currentPlanData.period,
                                  features: currentPlanData.features,
                                  planId: renewDetails?.planId,
                                  planValue: renewDetails?.planValue
                                }
                              } 
                            });
                          }
                        }}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Update Payment
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full lg:w-auto border-2 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary hover:border-primary/40 font-medium"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Manage Plan
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full lg:w-auto border-2 border-destructive/30 text-destructive hover:text-destructive hover:bg-destructive/10 hover:border-destructive/40 font-medium"
                        onClick={() => setShowCancelDialog(true)}
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Cancel Subscription
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Available Plans Section */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">Available Plans</h2>
                  <p className="text-sm sm:text-base text-muted-foreground mt-1">
                    Choose the Pharmasoft CS Director plan that best fits your needs.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {isLoadingPlans ? (
                    <div className="col-span-full text-center py-8 text-muted-foreground">
                      Loading plans...
                    </div>
                  ) : availablePlans.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-muted-foreground">
                      No plans available
                    </div>
                  ) : (
                    availablePlans.map((plan) => (
                    <Card
                      key={plan.name}
                      className={`relative overflow-hidden border shadow-sm flex flex-col ${
                        plan.isCurrent ? "border-primary/30" : ""
                      }`}
                    >
                      {plan.isPopular && (
                        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                            Most Popular
                          </span>
                        </div>
                      )}
                      <CardHeader className={plan.isPopular ? "pt-8 sm:pt-10" : ""}>
                        <CardTitle className="text-lg sm:text-xl text-foreground">{plan.name}</CardTitle>
                        <CardDescription className="flex flex-wrap items-baseline gap-1 text-base sm:text-lg mt-2">
                          <span className="text-xl sm:text-2xl font-bold text-foreground">{plan.price}</span>
                          <span className="text-sm sm:text-base text-muted-foreground">{plan.priceNote}</span>
                          <span className="text-sm sm:text-base text-muted-foreground">/{plan.period}</span>
                        </CardDescription>
                        {plan.isCurrent && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Next billing {nextBillingDate}
                          </p>
                        )}
                      </CardHeader>
                      <CardContent className="flex flex-col flex-1 space-y-6">
                        <ul className="space-y-2.5 flex-1">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2 text-sm text-foreground">
                              <div className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center mt-0.5 flex-shrink-0">
                                <Check className="w-3 h-3" />
                              </div>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        <Button
                          className={`w-full mt-auto ${
                            plan.isCurrent
                              ? "border-primary/20 text-primary hover:text-primary hover:bg-primary/5"
                              : "gradient-primary hover:opacity-90 transition-opacity"
                          }`}
                          variant={plan.isCurrent ? "outline" : "default"}
                          onClick={() => navigate("/payment", { 
                            state: { 
                              plan: {
                                ...plan,
                                planId: plan.planId,
                                planValue: plan.planValue
                              }
                            } 
                          })}
                        >
                          {plan.isCurrent ? "Manage Subscription" : `Upgrade to ${plan.name}`}
                        </Button>
                      </CardContent>
                    </Card>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Cancel Subscription Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader className="space-y-3">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600 border border-red-100">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <DialogTitle className="text-center text-xl">Cancel Subscription?</DialogTitle>
              <DialogDescription className="text-center text-muted-foreground">
                Are you sure you want to cancel your subscription? You&apos;ll lose access to all premium
                features at the end of your current billing period.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border bg-muted/50 p-4 text-sm text-foreground space-y-2">
              <p className="font-medium">You&apos;ll lose access to:</p>
              <ul className="space-y-1 text-muted-foreground">
                {["Unlimited projects", "Advanced analytics", "Priority support", "Custom integrations"].map(
                  (item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 text-xs">•</span>
                      <span>{item}</span>
                    </li>
                  )
                )}
              </ul>
            </div>

            <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-2">
              <DialogClose asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  Keep Subscription
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
                onClick={() => setShowCancelDialog(false)}
              >
                Yes, Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Footer */}
        <footer className="border-t bg-card mt-8 sm:mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
              <Link to="/about" className="hover:text-foreground transition-colors">
                About Us
              </Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/refund" className="hover:text-foreground transition-colors">
                Refund & Cancellation
              </Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">
                Contact Us
              </Link>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © {new Date().getFullYear()}{" "}
              <a
                href="https://kireetisoft.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground transition-colors underline"
              >
                KIREETI SOFT TECHNOLOGIES LIMITED.
              </a>{" "}
              All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </Layout>
  );
};

export default MyAccount;
